import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import './Chatbot.css';
import { playBot, playUser } from '../sounds';

// Reusable HTML for ceremony details + media-upload CTA
const ceremonyDetailsHTML = (studentId) => {
  const uploadHref = studentId ? `/upload/${studentId}` : '/upload';
  return `
    <div class="ceremony-details">
      <h3>Itinerary</h3>
      <p><strong>Date:</strong> Monday, May 4, 2026</p>
      <p><strong>Time:</strong> 6:00 PM to 8:00 PM</p>
      <p><strong>Location:</strong> Veterans Memorial High School Auditorium</p>
      <p><strong>Reception:</strong> Following the ceremony, in the cafeteria</p>
      <p><strong>Dress Code:</strong> Semi-formal</p>
      <p class="note">Share photos and video clips from your CTE program of study for the ceremony slideshow.</p>
      <a href="${uploadHref}" class="cta-button">Upload your memories &rarr;</a>
    </div>
  `;
};

// Chatbot steps
const STEPS = {
  WELCOME: 'welcome',
  ROLE_SELECT: 'role_select',
  STUDENT_ID: 'student_id',
  CONFIRM_STUDENT: 'confirm_student',
  ATTENDING: 'attending',
  PRONUNCIATION: 'pronunciation',
  PRONUNCIATION_TEXT: 'pronunciation_text',
  AUDIO_PRONUNCIATION: 'audio_pronunciation',
  GUEST_COUNT: 'guest_count',
  GUEST_DETAILS: 'guest_details',
  CONFIRM_RSVP: 'confirm_rsvp',
  THANK_YOU: 'thank_you',
  ERROR: 'error',
  SAVING: 'saving' // New step for saving indicator
};

function Chatbot({ onStudentIdSubmit, onRsvpSubmit, studentData, rsvpData, loading, errorMessage, hasUrlStudentId }) {
  // State
  const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [disableInput, setDisableInput] = useState(false);
  const [tempRsvpData, setTempRsvpData] = useState({
    attending: true,
    nameConfirmed: false,
    pronunciation: '',
    pronunciationAudio: null,
    guestCount: 0,
    guests: [],
    updatedAt: null,
    createdAt: null,
    completedSteps: []
  });
  const [currentGuest, setCurrentGuest] = useState(0);
  const [guestDetailStep, setGuestDetailStep] = useState('name'); // 'name' or 'relationship'
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs
  const chatEndRef = useRef(null);
  const initialMessageShown = useRef(false);
  const studentConfirmationShown = useRef(false);
  const studentIdPromptShown = useRef(false);
  const rolePromptShown = useRef(false);
  const userSelectedGuestCountRef = useRef(0);
  
  // Firebase references
  const dbRef = useRef(null);
  const sessionIdRef = useRef(null);
  
  // Use the shared Firestore client (single project)
  useEffect(() => {
    dbRef.current = db;
  }, []);
  
  // Debug function to log current state
  const logState = (message) => {
    console.log(`${message}: `, {
      currentStep,
      guestCount: tempRsvpData.guestCount,
      guests: tempRsvpData.guests,
      currentGuest,
      guestDetailStep,
      sessionId: sessionIdRef.current
    });
  };
  
  // Helper function to retrieve data from Firestore
  const fetchLatestData = async () => {
    if (!dbRef.current || !sessionIdRef.current) {
      console.warn("Cannot fetch data - Firebase or session not initialized");
      return null;
    }
    
    try {
      const docRef = doc(collection(dbRef.current, "rsvpAppointments"), sessionIdRef.current);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log("Fetched latest data:", data);
        return data;
      } else {
        console.warn("No document exists with session ID:", sessionIdRef.current);
        return null;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };
  
  // Initialize Firebase session
  const initializeFirebaseSession = async (studentData) => {
    if (!dbRef.current) {
      console.error("Firebase Firestore not available");
      return false;
    }
    
    // Create a session ID if not already created
    if (!sessionIdRef.current) {
      sessionIdRef.current = "session_" + Date.now() + "_" + Math.random().toString(36).substring(2, 9);
    }
    
    // Initialize the RSVP data with student information
    const initialData = {
      studentId: studentData?.id || null,
      studentName: studentData?.name || null,
      program: studentData?.program || null,
      attending: true,
      nameConfirmed: false,
      pronunciation: '',
      pronunciationAudio: null,
      guestCount: 0,
      guests: [],
      updatedAt: new Date(),
      createdAt: new Date(),
      status: "in_progress",
      completedSteps: []
    };
    
    // Create the document in Firestore
    try {
      const docRef = doc(collection(dbRef.current, "rsvpAppointments"), sessionIdRef.current);
      await setDoc(docRef, initialData);
      console.log("Firebase session initialized:", sessionIdRef.current);
      return true;
    } catch (error) {
      console.error("Error creating session:", error);
      return false;
    }
  };
  
  // Save progress to Firestore
  const saveToFirestore = async (updatedData = {}) => {
    // If Firebase not available, use onRsvpSubmit as fallback
    if (!dbRef.current || !sessionIdRef.current) {
      console.warn("Firebase unavailable, using fallback save method");
      
      const mergedData = {
        ...tempRsvpData,
        ...updatedData,
        updatedAt: new Date()
      };
      
      try {
        const success = await onRsvpSubmit(mergedData);
        return success;
      } catch (error) {
        console.error("Error in fallback save:", error);
        return false;
      }
    }
    
    // Show saving state
    setIsSaving(true);
    addBotMessage({
      text: "Saving your information...",
      delay: 0
    });
    
    // Add timestamp and merge with current data
    const dataToSave = {
      ...updatedData,
      updatedAt: new Date()
    };
    
    // Use the merge option to only update specified fields
    try {
      const docRef = doc(collection(dbRef.current, "rsvpAppointments"), sessionIdRef.current);
      await updateDoc(docRef, dataToSave);
      console.log("Data saved successfully:", dataToSave);
      
      // Show success message
      addBotMessage({
        text: "Information saved!",
        delay: 200
      });
      
      // Immediately fetch the updated data to confirm save was successful
      const latestData = await fetchLatestData();
      if (latestData) {
        // Verify specific fields that were updated
        let verificationFailed = false;
        
        // Check guest count if it was updated
        if (dataToSave.guestCount !== undefined && 
            latestData.guestCount !== dataToSave.guestCount) {
          console.error("Verification failed: Guest count mismatch", {
            saved: dataToSave.guestCount,
            retrieved: latestData.guestCount
          });
          verificationFailed = true;
        }
        
        if (verificationFailed) {
          console.warn("Data verification failed, but continuing");
          addBotMessage({
            text: "There might have been an issue with saving. Please check your information carefully.",
            delay: 200
          });
        }
        
        // Update local state with verified data from database
        setTempRsvpData(prev => ({
          ...prev,
          ...latestData,
        }));
      }
      
      setIsSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      
      // Show error but continue
      addBotMessage({
        text: "There was an issue saving your information, but we'll continue.",
        delay: 200
      });
      
      setIsSaving(false);
      return false;
    }
  };
  
  // Clear all messages and reset the chat
  const resetChat = () => {
    setMessages([]);
    initialMessageShown.current = false;
    studentConfirmationShown.current = false;
    studentIdPromptShown.current = false;
    setIsSaving(false);
    setCurrentStep(STEPS.WELCOME);
    
    // Reset RSVP data
    setTempRsvpData({
      attending: true,
      nameConfirmed: false,
      pronunciation: '',
      pronunciationAudio: null,
      guestCount: 0,
      guests: [],
      updatedAt: null,
      createdAt: null,
      completedSteps: []
    });
    
    setCurrentGuest(0);
    setGuestDetailStep('name');
    userSelectedGuestCountRef.current = 0;
    
    // Show welcome message
    setTimeout(() => {
      addBotMessage({
        text: "Welcome! Your destination is Success. This is the reservation desk for the VMHS CTE Senior Awards Ceremony, Class of 2026.",
        delay: 0
      });

      // If we don't have student data, show student ID prompt
      if (!studentData) {
        studentIdPromptShown.current = true;
        addBotMessage({
          text: "What's your student ID? I'll pull up your file.",
          delay: 500
        });
        setCurrentStep(STEPS.STUDENT_ID);
      }
    }, 100);
  };
  
  // Initialize with existing RSVP data if available
  useEffect(() => {
    if (rsvpData) {
      console.log("Setting initial RSVP data:", rsvpData);
      setTempRsvpData({
        ...rsvpData,
        nameConfirmed: true
      });
      if (typeof rsvpData.guestCount === 'number') {
        userSelectedGuestCountRef.current = rsvpData.guestCount;
      }
    }
  }, [rsvpData]);
  
  // Set initial welcome message
  useEffect(() => {
    if (!initialMessageShown.current) {
      initialMessageShown.current = true;
      addBotMessage({
        text: "Welcome! Your destination is Success. This is the reservation desk for the VMHS CTE Senior Awards Ceremony, Class of 2026.",
        delay: 500
      });
      addBotMessage({
        text: "Departure is Monday, May 4 at 6:00 PM. Let's confirm your reservation.",
        delay: 1100
      });
    }
  }, []);
  
  // Handle student data from URL or student ID input
  useEffect(() => {
    // Only proceed if not loading
    if (loading) return;
    
    // Skip if studentConfirmation already shown for this student
    if (studentData && !studentConfirmationShown.current) {
      studentConfirmationShown.current = true;
      
      // Initialize Firebase session with student data
      if (dbRef.current && !sessionIdRef.current) {
        initializeFirebaseSession(studentData);
      }
      
      // Ask for student confirmation
      setCurrentStep(STEPS.CONFIRM_STUDENT);
      addBotMessage({
        text: `I have ${studentData.name} on the shortlist${studentData.program ? `, ${studentData.program}` : ''}. Is this you?`,
        options: ["Yes, that's me!", "No, I'm someone else"],
        delay: 500
      });
    }
    // Show role selector first (bare URL with no studentId). Existing
    // /{studentId} URLs hit the studentData branch above and skip this.
    else if (!studentData && !hasUrlStudentId && !rolePromptShown.current && initialMessageShown.current && currentStep === STEPS.WELCOME) {
      rolePromptShown.current = true;
      addBotMessage({
        text: "Are you a CTE senior or a staff member?",
        options: ["I'm a CTE senior", "I'm staff"],
        delay: 500
      });
      setCurrentStep(STEPS.ROLE_SELECT);
    }
  }, [studentData, loading, currentStep]);
  
  // Handle error messages
  useEffect(() => {
    if (errorMessage && !loading) {
      addBotMessage({
        text: errorMessage,
        delay: 500
      });
      
      // Reset to ask for student ID again
      setCurrentStep(STEPS.STUDENT_ID);
    }
  }, [errorMessage, loading]);
  
  // Scroll the chat-messages container to its bottom as new bubbles arrive.
  // We deliberately do NOT call scrollIntoView — that walks up the scroll
  // chain and pulls the whole document down on first paint when the chat
  // sits below the fold.
  useEffect(() => {
    const end = chatEndRef.current;
    const scroller = end?.parentElement;
    if (scroller) {
      scroller.scrollTop = scroller.scrollHeight;
    }
  }, [messages]);
  
  // Add a message from the bot
  const addBotMessage = (messageObj) => {
    const { text, options, delay = 0, isHTML = false } = messageObj;
    
    setTimeout(() => {
      playBot();

      setMessages(prev => [
        ...prev, 
        { 
          id: Date.now(), 
          sender: 'bot', 
          text, 
          options,
          isHTML
        }
      ]);
      
      // Only keep input disabled for option messages
      setDisableInput(options ? true : false);
    }, delay);
  };
  
  // Add a message from the user
  const addUserMessage = (text) => {
    playUser();

    setMessages(prev => [
      ...prev, 
      { 
        id: Date.now(), 
        sender: 'user', 
        text 
      }
    ]);
  };
  
  // Handle option button clicks
  const handleOptionClick = (option) => {
    // Prevent interaction while saving
    if (isSaving) {
      return;
    }
    
    addUserMessage(option);
    setDisableInput(false);
    
    switch(currentStep) {
      case STEPS.ROLE_SELECT:
        if (option === "I'm staff") {
          window.location.replace('https://staff-rsvp.joshuabailey.us/');
        } else {
          studentIdPromptShown.current = true;
          addBotMessage({
            text: "What's your student ID? I'll pull up your file.",
            delay: 500
          });
          setCurrentStep(STEPS.STUDENT_ID);
        }
        break;
      case STEPS.CONFIRM_STUDENT:
        handleConfirmStudent(option);
        break;
      case STEPS.ATTENDING:
        handleAttending(option);
        break;
      case STEPS.PRONUNCIATION:
        if (option === "I'd like to upload an audio file" || option === "I'd like to upload a new audio file") {
          handleAudioUpload();
        } else if (option === "I'll type it instead") {
          setCurrentStep(STEPS.PRONUNCIATION_TEXT);
          addBotMessage({
            text: "Please type how your name should be pronounced:",
            delay: 500
          });
        } else if (option === "Keep current audio file") {
          // Keep existing audio file and move to guest count
          setCurrentStep(STEPS.GUEST_COUNT);
          askGuestCount();
        } else {
          setCurrentStep(STEPS.GUEST_COUNT);
          askGuestCount();
        }
        break;
      case STEPS.GUEST_COUNT:
        const numGuests = parseInt(option);
        if (!isNaN(numGuests) && numGuests >= 0 && numGuests <= 4) {
          handleGuestCount(numGuests);
        } else {
          addBotMessage({
            text: "Please select a valid number of guests (0-4).",
            options: ['0', '1', '2', '3', '4'],
            delay: 500
          });
        }
        break;
      case STEPS.CONFIRM_RSVP:
        handleConfirmRsvp(option);
        break;
      default:
        break;
    }
  };
  
  // Handle student confirmation
  const handleConfirmStudent = (option) => {
    if (option === "Yes, that's me!") {
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        nameConfirmed: true
      }));
      
      // Check if they've already RSVP'd
      if (rsvpData) {
        addBotMessage({
          text: `Great! I see you've already RSVP'd. You can review or modify your information.`,
          delay: 500
        });
        
        // Show them their existing RSVP information
        let html = `
          <div class="rsvp-summary">
            <h3>Your Current RSVP</h3>
            <p><strong>Attending:</strong> ${rsvpData.attending ? 'Yes' : 'No'}</p>
            <p><strong>Name Pronunciation:</strong> ${rsvpData.pronunciation || 'Not provided'}</p>
        `;
        
        // Add audio player if they have an audio pronunciation
        if (rsvpData.pronunciationAudio) {
          html += `
            <p><strong>Audio Pronunciation:</strong> Provided</p>
            <div class="audio-player">
              <audio controls src="${rsvpData.pronunciationAudio}"></audio>
            </div>
          `;
        }
        
        html += `<p><strong>Number of Guests:</strong> ${rsvpData.guestCount}</p>`;
        
        // Add guest information if available
        if (rsvpData.guests && rsvpData.guests.length > 0) {
          rsvpData.guests.forEach((guest, i) => {
            html += `
              <div class="guest-item">
                <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
                <p><strong>Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
              </div>
            `;
          });
        }
        
        // Format date properly - handle both Firestore timestamps and regular dates
        let dateString = '';
        if (rsvpData.updatedAt) {
          if (typeof rsvpData.updatedAt.toDate === 'function') {
            dateString = rsvpData.updatedAt.toDate().toLocaleDateString();
          } else if (rsvpData.updatedAt instanceof Date) {
            dateString = rsvpData.updatedAt.toLocaleDateString();
          }
        }
        
        html += `
            <p><small>Last updated: ${dateString}</small></p>
          </div>
        `;
        
        addBotMessage({
          text: html,
          isHTML: true,
          delay: 1000
        });
        
        addBotMessage({
          text: "Would you like to make changes to your RSVP?",
          options: ["Yes, update my RSVP", "No, keep it as is"],
          delay: 1500
        });
        
        setCurrentStep(STEPS.CONFIRM_RSVP);
      } else {
        // New RSVP - save confirmation and ask attendance first
        saveToFirestore({ nameConfirmed: true }).then(() => {
          setCurrentStep(STEPS.ATTENDING);
          askAttending();
        });
      }
    } else if (option === "No, I'm someone else") {
      // Not the right student - clear the URL and student data
      window.history.pushState(null, '', '/');
      
      // Clear student data in parent component
      onStudentIdSubmit(null);
      
      // Reset session ID
      sessionIdRef.current = null;
      
      // Reset all refs and state
      studentConfirmationShown.current = false;
      resetChat();
    }
  };
  
  // Ask whether the student will attend the ceremony.
  const askAttending = () => {
    addBotMessage({
      text: "Will you be able to attend the ceremony on Monday, May 4?",
      options: ["Yes, I'll be there", "No, I can't make it"],
      delay: 500
    });
  };

  // Handle the attending answer.
  const handleAttending = async (option) => {
    if (option === "Yes, I'll be there") {
      setTempRsvpData(prev => ({
        ...prev,
        attending: true,
        completedSteps: prev.completedSteps.includes("attending")
          ? prev.completedSteps
          : [...prev.completedSteps, "attending"]
      }));

      await saveToFirestore({
        attending: true,
        completedSteps: tempRsvpData.completedSteps.includes("attending")
          ? tempRsvpData.completedSteps
          : [...tempRsvpData.completedSteps, "attending"]
      });

      setCurrentStep(STEPS.PRONUNCIATION);
      askForPronunciation();
    } else if (option === "No, I can't make it") {
      // Save a complete "not attending" RSVP and jump to thank-you. The
      // canonical rsvps/{studentId} doc has to land too, otherwise the admin
      // dashboard treats them as a non-respondent.
      setIsSaving(true);

      const finalCompletedSteps = [...tempRsvpData.completedSteps, "attending", "confirmation"];
      const submittedAt = new Date();
      const notAttendingPayload = {
        attending: false,
        guestCount: 0,
        guests: [],
        pronunciation: '',
        pronunciationAudio: null,
        status: "confirmed",
        completedSteps: finalCompletedSteps,
        submittedAt
      };

      setTempRsvpData(prev => ({ ...prev, ...notAttendingPayload }));

      await saveToFirestore(notAttendingPayload);

      try {
        await onRsvpSubmit({
          ...tempRsvpData,
          ...notAttendingPayload,
          sourceSessionId: sessionIdRef.current
        });
      } catch (err) {
        console.error("Failed to promote not-attending RSVP to rsvps/{studentId}:", err);
      }

      setIsSaving(false);
      setCurrentStep(STEPS.THANK_YOU);
      updateStatusIndicator("confirmed");
      updateProgressBar(100);

      addBotMessage({
        text: "Thanks for letting us know — we'll miss you. If your plans change, just come back to this page to update your RSVP.",
        delay: 500
      });
    }
  };

  // Ask for name pronunciation
  const askForPronunciation = () => {
    addBotMessage({
      text: `So the announcer reads your name correctly when you cross the stage, type how it should sound. For example, "Jane DOE" or "juh-NELLE smith".`,
      delay: 500
    });
    
    // Check if they already have an audio pronunciation
    if (tempRsvpData.pronunciationAudio) {
      addBotMessage({
        text: `
          <div class="current-pronunciation">
            <p>You already have an audio pronunciation file:</p>
            <div class="audio-player">
              <audio controls src="${tempRsvpData.pronunciationAudio}"></audio>
            </div>
            <p>Would you like to keep this file or provide a new pronunciation?</p>
          </div>
        `,
        isHTML: true,
        delay: 1000
      });
      
      addBotMessage({
        text: "What would you like to do?",
        options: [
          "Keep current audio file", 
          "I'd like to upload a new audio file", 
          "I'll type it instead"
        ],
        delay: 1500
      });
    } else {
      addBotMessage({
        text: "You can type your pronunciation guide or upload an audio file.",
        options: ["I'd like to upload an audio file", "I'll type it instead"],
        delay: 1000
      });
    }
  };
  
  // Create a new function to handle audio upload
  const handleAudioUpload = () => {
    setCurrentStep(STEPS.AUDIO_PRONUNCIATION);
    
    addBotMessage({
      text: "Please upload a short audio file of your name pronunciation (MP3, WAV, or M4A).",
      delay: 500
    });
    
    // Simple file uploader HTML
    addBotMessage({
      text: `
        <div class="audio-uploader">
          <input type="file" id="audio-file" accept="audio/*" class="file-input" />
          <div id="upload-status"></div>
          <div id="audio-preview"></div>
          <button id="upload-button" class="upload-button" disabled>Upload File</button>
        </div>
      `,
      isHTML: true,
      delay: 1000
    });
    
    // Add event listeners
    setTimeout(() => {
      const fileInput = document.getElementById('audio-file');
      const uploadButton = document.getElementById('upload-button');
      const statusDiv = document.getElementById('upload-status');
      const previewDiv = document.getElementById('audio-preview');
      
      if (fileInput) {
        fileInput.addEventListener('change', (e) => {
          const file = e.target.files[0];
          if (!file) return;
          
          // Check file size (5MB max)
          if (file.size > 5 * 1024 * 1024) {
            if (statusDiv) statusDiv.innerHTML = '<span class="error">File is too large. Maximum size is 5MB.</span>';
            return;
          }
          
          // Check file type
          const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a'];
          if (!validTypes.includes(file.type)) {
            if (statusDiv) statusDiv.innerHTML = '<span class="error">Please select an MP3, WAV, or M4A file.</span>';
            return;
          }
          
          // Enable upload button when file selected
          if (uploadButton) uploadButton.disabled = false;
          
          // Show file info
          if (statusDiv) statusDiv.innerHTML = `Selected: ${file.name}`;
          
          // Create audio preview
          if (previewDiv) {
            const url = URL.createObjectURL(file);
            previewDiv.innerHTML = `
              <div class="audio-player">
                <audio controls src="${url}"></audio>
              </div>
            `;
            
            // Store the file for upload - use window to avoid React state issues
            window._tempPronunciationAudioFile = file;
          }
        });
      }
      
      if (uploadButton) {
        uploadButton.addEventListener('click', uploadAudioFile);
      }
    }, 1500);
  };

  // Function to upload the audio file
  const uploadAudioFile = async () => {
    const statusDiv = document.getElementById('upload-status');
    const file = window._tempPronunciationAudioFile;
    
    if (!file) {
      if (statusDiv) statusDiv.innerHTML = '<span class="error">Please select a file first.</span>';
      return;
    }
    
    try {
      setIsSaving(true);
      if (statusDiv) statusDiv.innerHTML = '<span class="uploading">Uploading... Please wait.</span>';
      
      // Upload to Firebase Storage
      const fileExt = file.name.split('.').pop();
      const storageRef = ref(storage, `pronunciations/${studentData.id}_${Date.now()}.${fileExt}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        pronunciationAudio: downloadURL,
        completedSteps: prev.completedSteps.includes("pronunciation") 
          ? prev.completedSteps 
          : [...prev.completedSteps, "pronunciation"]
      }));
      
      // Save to Firestore
      const saveSuccess = await saveToFirestore({ 
        pronunciationAudio: downloadURL,
        completedSteps: tempRsvpData.completedSteps.includes("pronunciation") 
          ? tempRsvpData.completedSteps 
          : [...tempRsvpData.completedSteps, "pronunciation"]
      });
      
      setIsSaving(false);
      
      if (statusDiv) {
        if (saveSuccess) {
          statusDiv.innerHTML = '<span class="success">Upload successful!</span>';
        } else {
          statusDiv.innerHTML = '<span class="error">Upload successful, but there was an issue saving. Continuing anyway.</span>';
        }
      }
      
      addUserMessage("I've uploaded my name pronunciation.");
      
      // Clear the temp file
      window._tempPronunciationAudioFile = null;
      
      // Move to next step
      setTimeout(() => {
        setCurrentStep(STEPS.GUEST_COUNT);
        askGuestCount();
      }, 1000);
    } catch (error) {
      setIsSaving(false);
      console.error('Error uploading audio:', error);
      if (statusDiv) statusDiv.innerHTML = '<span class="error">Upload failed. Please try again.</span>';
    }
  };
  
  // Ask for guest count
  const askGuestCount = () => {
    addBotMessage({
      text: `How many guests will be traveling with you? Up to 4.`,
      options: ['0', '1', '2', '3', '4'],
      delay: 500
    });
  };
  
  // Handle guest count response
  const handleGuestCount = async (count) => {
    // Prevent multiple submissions
    if (isSaving) return;
    
    // Disable input during processing
    setDisableInput(true);
    setIsSaving(true);
    
    // Ensure count is treated as a number
    const numGuests = parseInt(count);
    console.log('Setting guest count to:', numGuests);
    
    // CRITICAL FIX: Store the user's selection in a ref for later use
    userSelectedGuestCountRef.current = numGuests;
    
    // Create a properly sized array for the guests
    const guestsArray = [];
    for (let i = 0; i < numGuests; i++) {
      if (tempRsvpData.guests && tempRsvpData.guests[i]) {
        guestsArray.push({...tempRsvpData.guests[i]});
      } else {
        guestsArray.push({name: '', relationship: ''});
      }
    }
    
    // Update local state first
    setTempRsvpData(prev => ({
      ...prev,
      guestCount: numGuests,
      guests: guestsArray,
      completedSteps: prev.completedSteps.includes("guest_count") 
        ? prev.completedSteps 
        : [...prev.completedSteps, "guest_count"]
    }));
    
    // Save to Firebase
    try {
      await saveToFirestore({
        guestCount: numGuests,
        guests: guestsArray,
        completedSteps: tempRsvpData.completedSteps.includes("guest_count") 
          ? tempRsvpData.completedSteps 
          : [...tempRsvpData.completedSteps, "guest_count"]
      });
      
      // Add message about guest count
      addBotMessage({
        text: `You've selected ${numGuests} guest${numGuests !== 1 ? 's' : ''}.`,
        delay: 200
      });
      
      // Reset guest detail tracking
      setCurrentGuest(0);
      setGuestDetailStep('name');
      
      setIsSaving(false);
      setDisableInput(false);
      
      // CRITICAL FIX: Directly decide next step based on local guest count
      if (numGuests > 0) {
        console.log('Moving to GUEST_DETAILS step with', numGuests, 'guests');
        setCurrentStep(STEPS.GUEST_DETAILS);
        setTimeout(() => askGuestDetails(0, 'name'), 500);
      } else {
        console.log('No guests, showing summary');
        setTimeout(() => showRsvpSummary(), 500);
      }
    } catch (error) {
      console.error("Error in handleGuestCount:", error);
      setIsSaving(false);
      setDisableInput(false);
      
      addBotMessage({
        text: "Sorry, there was an issue processing your request. Let's try again.",
        delay: 200
      });
      
      // Add guest count options again
      setTimeout(() => askGuestCount(), 500);
    }
  };
  
  // Ask for guest details (name or relationship)
  const askGuestDetails = (guestIndex, detailType) => {
    console.log(`Asking for guest ${guestIndex} ${detailType}, total guests: ${tempRsvpData.guestCount}`);
    
    // Safety check - make sure we don't go beyond guest count
    if (guestIndex >= userSelectedGuestCountRef.current) {
      console.log(`Guest index ${guestIndex} exceeds guest count ${userSelectedGuestCountRef.current}, showing summary`);
      showRsvpSummary();
      return;
    }
    
    // Make absolutely sure we have valid guest data to work with
    if (!tempRsvpData.guests || !Array.isArray(tempRsvpData.guests) || tempRsvpData.guests.length <= guestIndex) {
      console.error("Missing or invalid guests array:", tempRsvpData.guests);
      
      // Create a properly sized array if missing
      const newGuestsArray = [];
      for (let i = 0; i < userSelectedGuestCountRef.current; i++) {
        if (tempRsvpData.guests && tempRsvpData.guests[i]) {
          newGuestsArray.push({...tempRsvpData.guests[i]});
        } else {
          newGuestsArray.push({name: '', relationship: ''});
        }
      }
      
      console.log("Created new guests array:", newGuestsArray);
      
      // Update state with the new array
      setTempRsvpData(prev => ({
        ...prev,
        guests: newGuestsArray
      }));
      
      // Use the new array for the current guest
      const guest = newGuestsArray[guestIndex] || {name: '', relationship: ''};
      
      if (detailType === 'name') {
        addBotMessage({
          text: `Please enter the name of Guest #${guestIndex + 1}:`,
          delay: 500
        });
        
        setGuestDetailStep('name');
      } else if (detailType === 'relationship') {
        // We can't reliably use guest.name here since we just created the guest object
        addBotMessage({
          text: `What is this person's relationship to you? (e.g., Parent, Sibling, Friend)`,
          delay: 500
        });
        
        setGuestDetailStep('relationship');
      }
      
      return;
    }
    
    const guest = tempRsvpData.guests[guestIndex];
    console.log(`Current guest data:`, guest);
    
    if (detailType === 'name') {
      // Display existing name if available
      const namePrompt = guest && guest.name 
        ? `Current name for Guest #${guestIndex + 1} is "${guest.name}". Enter a new name or press Enter to keep it:` 
        : `Please enter the name of Guest #${guestIndex + 1}:`;
      
      addBotMessage({
        text: namePrompt,
        delay: 500
      });
      
      // Set state to track what we're asking for
      setGuestDetailStep('name');
    } else if (detailType === 'relationship') {
      // Display existing relationship if available
      const relationshipPrompt = guest && guest.relationship 
        ? `Current relationship for ${guest.name || 'this person'} is "${guest.relationship}". Enter a new relationship or press Enter to keep it:` 
        : `What is ${guest.name || 'this person'}'s relationship to you? (e.g., Parent, Sibling, Friend)`;
      
      addBotMessage({
        text: relationshipPrompt,
        delay: 500
      });
      
      // Set state to track what we're asking for
      setGuestDetailStep('relationship');
    }
  };
  
  // Show RSVP summary
  const showRsvpSummary = async () => {
    console.log('Preparing to show summary');
    console.log('Current userSelectedGuestCount:', userSelectedGuestCountRef.current);
    
    // Show loading message
    addBotMessage({
      text: "Preparing your RSVP summary...",
      delay: 200
    });
    
    try {
      // CRITICAL FIX: Use the ref value directly rather than Firebase
      const finalGuestCount = userSelectedGuestCountRef.current;
      
      // Always use a properly sized guests array
      const finalGuests = [];
      for (let i = 0; i < finalGuestCount; i++) {
        if (tempRsvpData.guests && tempRsvpData.guests[i]) {
          finalGuests.push({...tempRsvpData.guests[i]});
        } else {
          finalGuests.push({name: 'Not specified', relationship: 'Not specified'});
        }
      }
      
      // Use the current state for the summary
      setCurrentStep(STEPS.CONFIRM_RSVP);
      
      const html = `
        <div class="rsvp-summary">
          <h3>Your RSVP Summary</h3>
          <p><strong>Name:</strong> ${studentData.name}</p>
          <p><strong>Program:</strong> ${studentData.program}</p>
          <p><strong>Name Pronunciation:</strong> ${tempRsvpData.pronunciation || 'Not provided'}</p>
          ${tempRsvpData.pronunciationAudio ? '<p><strong>Audio Pronunciation:</strong> Provided ✓</p>' : ''}
          <p><strong>Number of Guests:</strong> ${finalGuestCount}</p>
          ${finalGuests.length > 0 ? finalGuests.map((guest, i) => `
            <div class="guest-item">
              <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
              <p><strong>Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
            </div>
          `).join('') : ''}
        </div>
      `;
      
      addBotMessage({
        text: html,
        isHTML: true,
        delay: 300
      });
      
      addBotMessage({
        text: "Is this information correct?",
        options: ["Yes, submit my RSVP", "No, I need to make changes"],
        delay: 1000
      });
    } catch (error) {
      console.error("Error showing RSVP summary:", error);
      
      // Fall back to local ref value if there's an error
      const finalGuestCount = userSelectedGuestCountRef.current;
      
      const html = `
        <div class="rsvp-summary">
          <h3>Your RSVP Summary</h3>
          <p><strong>Name:</strong> ${studentData.name}</p>
          <p><strong>Program:</strong> ${studentData.program}</p>
          <p><strong>Name Pronunciation:</strong> ${tempRsvpData.pronunciation || 'Not provided'}</p>
          ${tempRsvpData.pronunciationAudio ? '<p><strong>Audio Pronunciation:</strong> Provided ✓</p>' : ''}
          <p><strong>Number of Guests:</strong> ${finalGuestCount}</p>
          ${tempRsvpData.guests && tempRsvpData.guests.length > 0 ? tempRsvpData.guests.slice(0, finalGuestCount).map((guest, i) => `
            <div class="guest-item">
              <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
              <p><strong>Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
            </div>
          `).join('') : ''}
        </div>
      `;
      
      addBotMessage({
        text: html,
        isHTML: true,
        delay: 300
      });
      
      addBotMessage({
        text: "Is this information correct?",
        options: ["Yes, submit my RSVP", "No, I need to make changes"],
        delay: 1000
      });
    }
  };
  
  // Handle RSVP confirmation
  const handleConfirmRsvp = async (option) => {
    if (option === "Yes, submit my RSVP") {
      setIsSaving(true);
      
      // Update status to confirmed
      setTempRsvpData(prev => ({
        ...prev,
        status: "confirmed",
        completedSteps: [...prev.completedSteps, "confirmation"]
      }));
      
      // Final submit - mark as completed
      const finalCompletedSteps = [...tempRsvpData.completedSteps, "confirmation"];
      const submittedAt = new Date();
      const success = await saveToFirestore({
        status: "confirmed",
        completedSteps: finalCompletedSteps,
        submittedAt
      });

      // Promote the confirmed answers into the canonical rsvps/{studentId}
      // doc — this is what the public RSVP page and admin dashboard read from.
      // The rsvpAppointments write above is the wizard scratchpad; without
      // this call, returning students look un-RSVP'd and the dashboard shows
      // them as non-respondents.
      try {
        await onRsvpSubmit({
          ...tempRsvpData,
          status: "confirmed",
          completedSteps: finalCompletedSteps,
          submittedAt,
          sourceSessionId: sessionIdRef.current
        });
      } catch (err) {
        console.error("Failed to promote RSVP to rsvps/{studentId}:", err);
      }

      setIsSaving(false);
      
      if (success) {
        setCurrentStep(STEPS.THANK_YOU);
        updateStatusIndicator("confirmed");
        updateProgressBar(100);
        
        addBotMessage({
          text: `Your reservation is confirmed. Departure is Monday, May 4 at 6:00 PM. See you at the ceremony!`,
          delay: 500
        });

        const ceremonyDetails = ceremonyDetailsHTML(studentData?.id);

        addBotMessage({
          text: ceremonyDetails,
          isHTML: true,
          delay: 1000
        });
      } else {
        // Even if Firestore save fails, still show the success message but log the error
        setCurrentStep(STEPS.THANK_YOU);
        updateStatusIndicator("confirmed");
        updateProgressBar(100);

        addBotMessage({
          text: "Saved a copy of your reservation. If anything looks off later, just come back to this page.",
          delay: 500
        });

        addBotMessage({
          text: ceremonyDetailsHTML(studentData?.id),
          isHTML: true,
          delay: 1000
        });
      }
    } else if (option === "No, keep it as is") {
      setCurrentStep(STEPS.THANK_YOU);

      addBotMessage({
        text: "Got it. Your reservation is unchanged. See you at the ceremony.",
        delay: 500
      });

      addBotMessage({
        text: ceremonyDetailsHTML(studentData?.id),
        isHTML: true,
        delay: 1000
      });
    } else if (option === "Yes, update my RSVP") {
      // Start the update flow from the attending question so users can flip
      // between attending / not attending without losing prior answers.
      setCurrentStep(STEPS.ATTENDING);
      askAttending();
    } else {
      setCurrentStep(STEPS.ATTENDING);
      askAttending();
    }
  };
  
  // Handle input submission
  const handleInputSubmit = (e) => {
    e.preventDefault();
    
    // Prevent interaction while saving
    if (isSaving) {
      return;
    }
    
    // Don't submit empty inputs (except for keeping existing guest data)
    if (!userInput.trim() && 
        !(currentStep === STEPS.GUEST_DETAILS && 
          ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
           (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship)))) {
      return;
    }
    
    // Add the user's message to the chat (empty inputs are for keeping existing values)
    if (userInput.trim()) {
      addUserMessage(userInput);
    } else {
      // For empty input to keep existing values, show a message
      const keepingValue = guestDetailStep === 'name' 
        ? tempRsvpData.guests[currentGuest]?.name 
        : tempRsvpData.guests[currentGuest]?.relationship;
      addUserMessage(`Keep "${keepingValue}"`);
    }
    
    // Handle the input based on current step
    switch (currentStep) {
      case STEPS.STUDENT_ID:
        handleStudentIdSubmit();
        break;
      case STEPS.PRONUNCIATION_TEXT:
        handlePronunciationTextSubmit();
        break;
      case STEPS.GUEST_DETAILS:
        handleGuestDetailsSubmit();
        break;
      case STEPS.GUEST_COUNT:
        // Handle numeric inputs for guest count
        const count = parseInt(userInput.trim());
        if (!isNaN(count) && count >= 0 && count <= 4) {
          handleGuestCount(count);
        } else {
          addBotMessage({
            text: "Please select a valid number of guests (0-4).",
            options: ['0', '1', '2', '3', '4'],
            delay: 500
          });
        }
        break;
      default:
        // For unhandled steps, let the user know
        addBotMessage({
          text: "Please use the provided options to continue.",
          delay: 500
        });
        break;
    }
    
    // Clear the input field
    setUserInput('');
  };
  
  // Handle student ID submission
  const handleStudentIdSubmit = () => {
    const id = userInput.trim();

    if (!/^\d+$/.test(id)) {
      addBotMessage({
        text: "Please enter a valid student ID (numbers only).",
        delay: 500
      });
      return;
    }

    addBotMessage({
      text: "Looking up your information...",
      delay: 500
    });

    onStudentIdSubmit(id);
  };
  
  // Handle pronunciation text submission
  const handlePronunciationTextSubmit = async () => {
    const pronunciation = userInput.trim();
    
    // Disable input during processing
    setDisableInput(true);
    setIsSaving(true);
    
    // Update local state
    setTempRsvpData(prev => ({
      ...prev,
      pronunciation: pronunciation,
      completedSteps: prev.completedSteps.includes("pronunciation") 
        ? prev.completedSteps 
        : [...prev.completedSteps, "pronunciation"]
    }));
    
    // Save to Firebase
    const success = await saveToFirestore({
      pronunciation: pronunciation,
      completedSteps: tempRsvpData.completedSteps.includes("pronunciation") 
        ? tempRsvpData.completedSteps 
        : [...tempRsvpData.completedSteps, "pronunciation"]
    });
    
    setDisableInput(false);
    setIsSaving(false);
    
    addBotMessage({
      text: `Thanks! I've recorded your pronunciation guide: "${pronunciation}"`,
      delay: 500
    });
    
    setCurrentStep(STEPS.GUEST_COUNT);
    askGuestCount();
  };
  
  // Handle guest details submission (name or relationship)
  const handleGuestDetailsSubmit = async () => {
    // Prevent multiple submissions
    if (isSaving) return;
    
    setDisableInput(true);
    setIsSaving(true);
    
    logState("Before guest details update");
    
    // Make a copy of the guests array
    const updatedGuests = [...tempRsvpData.guests];
    const currentGuestData = updatedGuests[currentGuest] || { name: '', relationship: '' };
    
    if (guestDetailStep === 'name') {
      // If input is not empty, update the name
      if (userInput.trim()) {
        currentGuestData.name = userInput.trim();
      }
      
      // Update the guests array
      updatedGuests[currentGuest] = currentGuestData;
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        guests: updatedGuests
      }));
      
      // Save to Firebase
      const success = await saveToFirestore({
        guests: updatedGuests
      });
      
      setDisableInput(false);
      setIsSaving(false);
      
      console.log(`Updated guest ${currentGuest} name to "${currentGuestData.name}"`);
      
      // Move to asking for relationship
      setGuestDetailStep('relationship');
      askGuestDetails(currentGuest, 'relationship');
      
    } else if (guestDetailStep === 'relationship') {
      // If input is not empty, update the relationship
      if (userInput.trim()) {
        currentGuestData.relationship = userInput.trim();
      }
      
      // Update the guests array
      updatedGuests[currentGuest] = currentGuestData;
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        guests: updatedGuests
      }));
      
      // Save to Firebase
      const success = await saveToFirestore({
        guests: updatedGuests
      });
      
      setDisableInput(false);
      setIsSaving(false);
      
      console.log(`Updated guest ${currentGuest} relationship to "${currentGuestData.relationship}"`);
      
      // Move to next guest
      const nextGuest = currentGuest + 1;
      console.log(`Moving to guest ${nextGuest} of ${userSelectedGuestCountRef.current}`);
      
      if (nextGuest < userSelectedGuestCountRef.current) {
        // More guests to process
        setCurrentGuest(nextGuest);
        setGuestDetailStep('name');
        
        // Small delay to ensure state updates
        setTimeout(() => {
          askGuestDetails(nextGuest, 'name');
        }, 250);
      } else {
        // All guests processed, show summary
        console.log('All guests processed, showing summary');
        showRsvpSummary();
      }
    }
    
    logState("After guest details update");
  };
  
  // Helpers for status indicator and progress
  const updateStatusIndicator = (status) => {
    const statusEl = document.getElementById("chatbot-status");
    if (!statusEl) return;
    
    statusEl.innerHTML = '';
    
    if (status === "pending") {
      statusEl.innerHTML = `
        <div class="status-indicator pending">
          <span class="status-icon">⌛</span>
          <span class="status-text">RSVP Pending</span>
        </div>
      `;
    } else if (status === "confirmed") {
      statusEl.innerHTML = `
        <div class="status-indicator confirmed">
          <span class="status-icon">✓</span>
          <span class="status-text">RSVP Confirmed</span>
        </div>
      `;
    }
  };
  
  const updateProgressBar = (percent) => {
    const progressFill = document.querySelector(".progress-fill");
    const progressText = document.querySelector(".progress-text");
    
    if (progressFill && progressText) {
      progressFill.style.width = `${percent}%`;
      progressText.textContent = `${Math.round(percent)}% complete`;
    }
  };
  
  const calculateProgress = () => {
    // Define total steps for progress calculation
    const totalSteps = 7; // Update with the correct number of steps
    const completedStepCount = tempRsvpData.completedSteps.length;
    
    // Cap at 95% until actual confirmation
    if (tempRsvpData.status !== "confirmed" && !tempRsvpData.completedSteps.includes("confirmation")) {
      return Math.min(Math.round((completedStepCount / totalSteps) * 95), 95);
    }
    
    return Math.min(Math.round((completedStepCount / totalSteps) * 100), 100);
  };
  
  return (
    <div className="chatbot-container">
      <div className="chat-messages">
        {messages.map(message => (
          <div key={message.id} className={`message ${message.sender}-message`}>
            {message.isHTML ? (
              <div dangerouslySetInnerHTML={{ __html: message.text }} />
            ) : (
              <div className="message-text">{message.text}</div>
            )}
            
            {message.options && (
              <div className="message-options">
                {message.options.map((option, index) => (
                  <button 
                    key={index} 
                    className="option-button"
                    onClick={() => handleOptionClick(option)}
                    disabled={isSaving}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      
      <div className="chatbot-status" id="chatbot-status">
        <div className="status-indicator pending">
          <span className="status-icon">⌛</span>
          <span className="status-text">RSVP Pending</span>
        </div>
      </div>
      
      <form className="chat-input" onSubmit={handleInputSubmit}>
        <input
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder={
            isSaving
              ? "Saving data, please wait..."
              : currentStep === STEPS.GUEST_DETAILS && 
                ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
                (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship))
                ? "Press Enter to keep current value or type a new one..."
                : disableInput 
                  ? "Please select an option above..." 
                  : "Type your response..."
          }
          disabled={disableInput || currentStep === STEPS.THANK_YOU || isSaving}
        />
        <button 
          type="submit"
          disabled={
            isSaving ||
            disableInput || 
            (userInput.trim() === '' && 
             !(currentStep === STEPS.GUEST_DETAILS && 
               ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
                (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship)))) || 
            currentStep === STEPS.THANK_YOU
          }
        >
          Send
        </button>
      </form>
    </div>
  );
}

export default Chatbot;